/**
 * CR229/CR308: Sample Deal Picker
 * 
 * Dropdown to select a real deal for preview.
 * Filtered by deal type, shows merchant + headline snippet.
 * 
 * CR308: Added Live/Sample toggle with Reality Badge.
 */

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Database, FileQuestion, Radio, FlaskConical } from "lucide-react";
import type { SampleDeal, DealDataSourceMode } from "@/hooks/useDealCardTemplateEditor";
import { safeStr } from "@/lib/safeString";

interface SampleDealPickerProps {
  samples: SampleDeal[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  loading: boolean;
  error: string | null;
  dealType: string;
  /** CR308: Current data source mode */
  mode: DealDataSourceMode;
  /** CR308: Callback to change mode */
  onModeChange: (mode: DealDataSourceMode) => void;
  /** CR308: Name of the view being queried */
  viewName?: string;
}

/**
 * CR308: Reality Badge showing current data source.
 */
function RealityBadge({ 
  mode, 
  viewName, 
  count 
}: { 
  mode: DealDataSourceMode; 
  viewName?: string; 
  count: number;
}) {
  const isLive = mode === "live";
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge 
            variant={isLive ? "default" : "secondary"}
            className={`gap-1 text-[10px] ${isLive ? "bg-emerald-600 hover:bg-emerald-700" : ""}`}
          >
            {isLive ? <Radio className="h-3 w-3" /> : <FlaskConical className="h-3 w-3" />}
            {isLive ? "LIVE" : "SAMPLE"}
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs">
          <div className="space-y-1">
            <div className="font-medium">{isLive ? "Live Production Data" : "Sample Data"}</div>
            <div className="text-muted-foreground">View: {viewName}</div>
            <div className="text-muted-foreground">{count} deals loaded</div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function SampleDealPicker({
  samples,
  selectedId,
  onSelect,
  loading,
  error,
  dealType,
  mode,
  onModeChange,
  viewName = mode === "live" ? "v_deals_live" : "v_layout_studio_sample_deals",
}: SampleDealPickerProps) {
  // CR405: Ensure all string inputs are safe using shared utility
  const safeDealType = safeStr(dealType, "UNKNOWN");
  const safeSamples = Array.isArray(samples) ? samples : [];
  if (loading) {
    return <Skeleton className="h-9 w-full" />;
  }
  
  if (error) {
    return (
      <div className="text-xs text-destructive flex items-center gap-1">
        <FileQuestion className="h-3.5 w-3.5" />
        Failed to load: {error}
      </div>
    );
  }
  
  return (
    <div className="space-y-2">
      {/* CR308: Mode Toggle + Reality Badge */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-muted-foreground">Data Source</label>
          <ToggleGroup 
            type="single" 
            value={mode} 
            onValueChange={(v) => v && onModeChange(v as DealDataSourceMode)}
            size="sm"
            className="h-7"
          >
            <ToggleGroupItem value="sample" className="text-xs px-2 h-6">
              <FlaskConical className="h-3 w-3 mr-1" />
              Sample
            </ToggleGroupItem>
            <ToggleGroupItem value="live" className="text-xs px-2 h-6">
              <Radio className="h-3 w-3 mr-1" />
              Live
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
        <RealityBadge mode={mode} viewName={viewName} count={safeSamples.length} />
      </div>
      
      {/* Deal Selector */}
      {safeSamples.length === 0 ? (
        <div className="text-xs text-muted-foreground flex items-center gap-1 p-2 border rounded bg-muted/30">
          <Database className="h-3.5 w-3.5" />
          No {safeDealType} deals found in {mode === "live" ? "production" : "staging"}.
        </div>
      ) : (
        <>
          <Select value={selectedId ?? undefined} onValueChange={onSelect}>
            <SelectTrigger className="h-9">
              <SelectValue placeholder={`${mode === "live" ? "Live" : "Sample"}: Select deal...`} />
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              {safeSamples.map((sample) => (
                <SelectItem key={sample.id} value={sample.id}>
                  <div className="flex items-center gap-2 max-w-[300px]">
                    <span className="font-medium truncate">{sample?.merchant ?? "Unknown"}</span>
                    <span className="text-muted-foreground text-xs truncate">
                      {sample?.headlineSnippet ?? ""}
                    </span>
                    <Badge variant="outline" className="text-[9px] ml-auto shrink-0">
                      {sample?.sourceType ?? "unknown"}
                    </Badge>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[10px] text-muted-foreground">
            {safeSamples.length} {safeDealType} deals from {viewName}
          </p>
        </>
      )}
    </div>
  );
}
